/**
 * Mesure d'audience Google Analytics 4.
 *
 * ⚖ Conformité CNIL : RIEN n'est chargé ni envoyé avant que le visiteur ait
 * ACCEPTÉ (mode de consentement « de base ») — ni script, ni cookie, ni ping
 * sans cookie. Refuser est aussi simple qu'accepter (`BandeauCookies`), le
 * choix se rouvre à tout moment (« Gérer les cookies », `rouvrirChoixCookies`)
 * et il expire au bout de 13 mois, comme les cookies `_ga`.
 *
 * 🔒 Ce qui part chez Google est NETTOYÉ (`adresseNettoyee`) : jamais un
 * jeton (lien de partage, invitation, réinitialisation de mot de passe),
 * jamais une requête d'URL hors `utm_*`, jamais un contenu de projet. Les
 * pages qui portent un jeton dans leur CHEMIN ne chargent pas du tout la
 * mesure (`cheminExclu`).
 *
 * Sans `NEXT_PUBLIC_GA_ID` (figée AU BUILD, comme toute NEXT_PUBLIC_), tout
 * ceci est inerte : pas de bandeau, pas de script.
 */

export const GA_ID = process.env.NEXT_PUBLIC_GA_ID?.trim() || "";

const CLE_CHOIX = "synoplix-consentement-mesure";
/** 13 mois : durée maximale d'un consentement et d'un cookie de mesure (CNIL). */
export const DUREE_CHOIX_MS = 395 * 24 * 3600 * 1000;
export const EVENEMENT_ROUVRIR = "synoplix:choix-cookies";
export const EVENEMENT_CHOIX = "synoplix:choix-cookies-fait";

export type Choix = "accepte" | "refuse";

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

/** Le choix enregistré, ou `null` s'il manque ou a expiré. */
export function lireChoix(): Choix | null {
  if (typeof window === "undefined") return null;
  try {
    const brut = window.localStorage.getItem(CLE_CHOIX);
    if (!brut) return null;
    const { choix, date } = JSON.parse(brut) as { choix?: string; date?: number };
    if ((choix !== "accepte" && choix !== "refuse") || typeof date !== "number") return null;
    if (Date.now() - date > DUREE_CHOIX_MS) return null;
    return choix;
  } catch {
    return null;
  }
}

export function enregistrerChoix(choix: Choix): void {
  try {
    window.localStorage.setItem(CLE_CHOIX, JSON.stringify({ choix, date: Date.now() }));
  } catch {
    /* navigation privée : le choix ne vaut que pour cette page */
  }
  window.dispatchEvent(new CustomEvent(EVENEMENT_CHOIX, { detail: choix }));
  if (choix === "refuse") effacerCookiesGa();
}

/** Rouvre le bandeau (lien « Gérer les cookies »). */
export function rouvrirChoixCookies(): void {
  window.dispatchEvent(new Event(EVENEMENT_ROUVRIR));
}

/** Retrait du consentement : on efface les cookies `_ga*` déjà posés. */
function effacerCookiesGa(): void {
  const domaines = ["", location.hostname, `.${location.hostname}`, `.${location.hostname.split(".").slice(-2).join(".")}`];
  for (const c of document.cookie.split(";")) {
    const nom = c.split("=")[0].trim();
    if (!nom.startsWith("_ga")) continue;
    for (const d of domaines) {
      document.cookie = `${nom}=; Max-Age=0; path=/${d ? `; domain=${d}` : ""}`;
    }
  }
}

/** Chemins qui portent un SECRET : la mesure n'y est jamais chargée. */
export function cheminExclu(chemin: string): boolean {
  return /^\/(partage|invitation|reset-password|reinitialiser)(\/|$)/.test(chemin);
}

/**
 * Adresse envoyée à Google : identifiants de projet et jetons remplacés, et
 * requête réduite aux seuls paramètres de campagne (`utm_*`).
 */
export function adresseNettoyee(href: string): string {
  try {
    const u = new URL(href);
    const chemin = u.pathname
      // jetons et identifiants (cuid, uuid, hex long) → marqueur
      .replace(/\/(partage|invitation)\/[^/]+/g, "/$1/:jeton")
      .replace(/\/(c[a-z0-9]{20,}|[0-9a-f]{8}-[0-9a-f-]{27,}|[0-9a-f]{24,})(?=\/|$)/gi, "/:id");
    const garder = new URLSearchParams();
    u.searchParams.forEach((v, k) => {
      if (k.startsWith("utm_")) garder.set(k, v);
    });
    const q = garder.toString();
    return `${u.origin}${chemin}${q ? `?${q}` : ""}`;
  } catch {
    return "";
  }
}

let charge = false;

/** Charge gtag (une fois) — à n'appeler qu'APRÈS acceptation. */
export function chargerGa(): void {
  if (charge || !GA_ID || typeof window === "undefined") return;
  if (cheminExclu(location.pathname)) return;
  // Réacceptation après un refus dans la même page : on rouvre la vanne.
  (window as unknown as Record<string, unknown>)[`ga-disable-${GA_ID}`] = false;
  charge = true;
  if (typeof window.gtag === "function") return; // script déjà là
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments);
  };
  window.gtag("js", new Date());
  // Avant toute mesure automatique : l'adresse nettoyée (voir `pageVue`).
  window.gtag("set", { page_location: adresseNettoyee(location.href) });
  window.gtag("config", GA_ID, {
    // Les pages vues sont envoyées à la main, adresse nettoyée (`pageVue`).
    send_page_view: false,
    // Ni signaux Google, ni personnalisation publicitaire : mesure seule.
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
    // 13 mois au plus (CNIL).
    cookie_expires: Math.floor(DUREE_CHOIX_MS / 1000),
  });
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(GA_ID)}`;
  document.head.appendChild(s);
}

/** Retrait du consentement après coup : plus rien ne part (sans recharger). */
export function couperGa(): void {
  if (!GA_ID || typeof window === "undefined") return;
  (window as unknown as Record<string, unknown>)[`ga-disable-${GA_ID}`] = true;
  charge = false;
}

/** Vrai quand la mesure est chargée et autorisée. */
function actif(): boolean {
  return charge && typeof window !== "undefined" && typeof window.gtag === "function" && !cheminExclu(location.pathname);
}

/**
 * Page vue, adresse nettoyée. `chemin` : celui que donne le routeur — la barre
 * d'adresse peut être encore l'ancienne quand la nouvelle page s'affiche
 * (une page vue « /login » partait après la connexion).
 */
export function pageVue(chemin?: string): void {
  if (!actif()) return;
  if (chemin && cheminExclu(chemin)) return;
  const page_location = adresseNettoyee(chemin ? `${location.origin}${chemin}${location.search}` : location.href);
  if (!page_location) return;
  // L'adresse nettoyée devient la référence de TOUT ce que gtag envoie
  // ensuite — y compris ses mesures automatiques (défilement, clics sortants,
  // et la page vue « historique » de la mesure améliorée si elle est restée
  // active dans Google Analytics, qui partait sinon avec l'adresse BRUTE :
  // un identifiant de projet a été vu passer le 04/10/2026).
  window.gtag!("set", { page_location });
  window.gtag!("event", "page_view", {
    page_location,
    page_title: document.title,
    page_referrer: document.referrer ? adresseNettoyee(document.referrer) : undefined,
  });
}

/**
 * Événement. Sans consentement (ou sans ID), ne fait RIEN : pas de file
 * d'attente qui partirait après coup.
 *
 * Paramètres : des catégories et des montants, jamais un contenu saisi
 * (nom de projet, e-mail, adresse…).
 */
export function suivre(evenement: string, parametres: Record<string, string | number | boolean | undefined> = {}): void {
  if (!actif()) return;
  window.gtag!("event", evenement, parametres);
}
